import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, db } from '../firebase';
import { collection, doc, addDoc, updateDoc, deleteDoc, query, orderBy, onSnapshot } from "firebase/firestore";
import { FiMoreHorizontal, FiTrash2, FiEdit, FiEdit3 } from "react-icons/fi";
import { LuClipboardList, LuWandSparkles } from "react-icons/lu";
import './sidebar.css';

function Sidebar({ onReset }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [displayName, setDisplayName] = useState("");
  const [profilePic, setProfilePic] = useState("/defaultProfile.png");

  const [conversations, setConversations] = useState([]);
  const [activeMenu, setActiveMenu] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [hoveringMenu, setHoveringMenu] = useState(false);
  const [user, setUser] = useState(null);
  const [loadingChats, setLoadingChats] = useState(true);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [chatToDeleteTitle, setChatToDeleteTitle] = useState("");

  const [renamingChatId, setRenamingChatId] = useState(null);
  const [renamingTitle, setRenamingTitle] = useState("");

  const showSidebar = ["/chat", "/questionnaires", "/questionnaire", "/suggestions"].some((path) =>
    location.pathname.startsWith(path)
  );

  const activeChatId = location.pathname === "/chat"
    ? localStorage.getItem("mindly_active_chat_id")
    : null;

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((u) => {
      if (u) {
        setUser(u);

        const userRef = doc(db, "users", u.uid);
        const unsubscribeProfile = onSnapshot(
          userRef,
          (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data();

              setDisplayName(
                data.displayName ||
                u.displayName ||
                u.email?.split("@")[0] ||
                "User"
              );
              setProfilePic(data.profilePic || u.photoURL || "/defaultProfile.png");
            } else {
              setDisplayName(u.displayName || u.email?.split("@")[0] || "User");
              setProfilePic(u.photoURL || "/defaultProfile.png");
            }
          },
        );

        return () => unsubscribeProfile();
      } else {
        setUser(null);
        setDisplayName("");
        setProfilePic("/defaultProfile.png");
        setConversations([]);
        setLoadingChats(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!user) {
      setConversations([]);
      setLoadingChats(false);
      return;
    }

    const chatsRef = collection(db, "users", user.uid, "chats");
    const q = query(chatsRef, orderBy("timestamp", "desc"));

    setLoadingChats(true);

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const chats = [];
        querySnapshot.forEach((doc) => {
          chats.push({ id: doc.id, ...doc.data() });
        });
        setConversations(chats);
        setLoadingChats(false);
      },
      () => setLoadingChats(false)
    );

    return () => unsubscribe();
  }, [user]);

  const handleNewChat = async () => {

    try {
      const chatsRef = collection(db, "users", user.uid, "chats");
      const newChat = {
        title: null,
        messages: [],
        tone: "pending",
        language: "en-US",
        timestamp: new Date(),
        titleGenerated: false,
      };
      const docRef = await addDoc(chatsRef, newChat);
      localStorage.setItem("mindly_active_chat_id", docRef.id);
      if (onReset) onReset();
      window.dispatchEvent(new Event("chat-change"));
      navigate("/chat");
    } catch {}
  };

  const handleLoadConversation = (chatId) => {
    if (chatId === activeChatId) return;
    localStorage.setItem("mindly_active_chat_id", chatId);
    if (onReset) onReset();
    window.dispatchEvent(new Event("chat-change"));
    navigate("/chat");
  };

  const handleStartRename = (chatId, currentTitle) => {
    setRenamingChatId(chatId);
    setRenamingTitle(currentTitle || "Untitled Chat");
  };

  const handleRenameKeyDown = async (e) => {
    if (e.key === "Enter") {
      await applyRename();
    } else if (e.key === "Escape") {
      setRenamingChatId(null);
      setRenamingTitle("");
    }
  };

  const applyRename = async () => {
    if (!renamingTitle.trim()) return;
    try {
      const chatDocRef = doc(db, "users", user.uid, "chats", renamingChatId);
      await updateDoc(chatDocRef, { title: renamingTitle.trim() });
    } catch {}
    setRenamingChatId(null);
    setRenamingTitle("");
  };

  const confirmDelete = (chatId, title) => {
    setChatToDelete(chatId);
    setChatToDeleteTitle(title || "Untitled Chat");
    setShowDeleteModal(true);
  };

  const executeDelete = async () => {
    try {
      const chatDocRef = doc(db, "users", user.uid, "chats", chatToDelete);
      await deleteDoc(chatDocRef);
      if (chatToDelete === activeChatId) {
        localStorage.removeItem("mindly_active_chat_id");
        if (onReset) onReset();
        navigate("/suggestions");
      }
    } catch {}
    setShowDeleteModal(false);
    setChatToDelete(null);
    setActiveMenu(null);
    setHoverIndex(null);
  };

  const isHovering = (i) => hoverIndex === i || (activeMenu === i && hoveringMenu);
  const isCurrentPage = (route) => location.pathname.startsWith(route);

  if (!showSidebar) return null;
  return (
    <>
      <div className="sidebar">
        <div className="sidebar-bottom">
          <button className="sidebar-btn new-chat-btn" onClick={handleNewChat}>
            <FiEdit className="icon" /> New Chat
          </button>
          <button
            className={`sidebar-btn ${isCurrentPage("/suggestions") ? "active-chat" : ""}`}
            onClick={() => {
              if (onReset) onReset();
              window.dispatchEvent(new Event("end-session"));
              navigate("/suggestions");
            }}
          >
            <LuWandSparkles size={18} /> Suggestions
          </button>

          <button
            className={`sidebar-btn ${
              isCurrentPage("/questionnaires") || isCurrentPage("/questionnaire") ? "active-chat" : ""
            }`}
            onClick={() => {
              if (!isCurrentPage("/questionnaires") && !isCurrentPage("/questionnaire")) {
                if (onReset) onReset();
                window.dispatchEvent(new Event("end-session"));
                navigate("/questionnaires");
              }
            }}
          >
            <LuClipboardList size={18} /> Questionnaires
          </button>
        </div>

        <div className="sidebar-divider-top" />
        <div className="sidebar-scroll-area">
          <div className="sidebar-section-label">Chats</div>
          <div className="sidebar-section">
            {loadingChats ? (
              <div className="sidebar-loading">Loading chats...</div>
            ) : conversations.length > 0 ? (
              conversations.map((convo, i) => {
                const displayTitle = convo.title?.trim() || "New Chat";
                return (
                  <div
                    key={convo.id}
                    className={`chat-entry-wrapper ${convo.id === activeChatId ? "active-chat" : ""}`}
                    onMouseEnter={() => setHoverIndex(i)}
                    onMouseLeave={() => {
                      setTimeout(() => {
                        if (!hoveringMenu) {
                          setHoverIndex(null);
                          setActiveMenu(null);
                        }
                      }, 150);
                    }}
                  >
                    <div className="chat-entry-row">
                      <button
                        className="chat-entry-btn"
                        onClick={() => handleLoadConversation(convo.id)}
                        disabled={convo.id === activeChatId}
                      >
                        {renamingChatId === convo.id ? (
                          <input
                            type="text"
                            className="rename-input"
                            value={renamingTitle}
                            autoFocus
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => setRenamingTitle(e.target.value)}
                            onKeyDown={handleRenameKeyDown}
                            onBlur={() => setRenamingChatId(null)}
                          />
                        ) : (
                          displayTitle
                        )}
                      </button>

                      {isHovering(i) && (
                        <button
                          className="menu-icon-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            document.documentElement.style.setProperty('--dropdown-top', `${rect.bottom + window.scrollY}px`);
                            document.documentElement.style.setProperty('--dropdown-left', `${rect.left}px`);
                            setActiveMenu(activeMenu === i ? null : i);
                          }}
                        >
                          <FiMoreHorizontal />
                        </button>
                      )}
                    </div>

                    {activeMenu === i && (
                      <div
                        className="chat-dropdown dropdown-above"
                        onMouseEnter={() => setHoveringMenu(true)}
                        onMouseLeave={() => {
                          setHoveringMenu(false);
                          setActiveMenu(null);
                          setHoverIndex(null);
                        }}
                      >
                        <div className="dropdown-item" onClick={() => handleStartRename(convo.id, displayTitle)}>
                          <FiEdit3 className="icon" /> Rename
                        </div>
                        <div className="dropdown-item delete" onClick={() => confirmDelete(convo.id, displayTitle)}>
                          <FiTrash2 className="icon" /> Delete
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="sidebar-chat-placeholder">No chats yet.</div>
            )}
          </div>
        </div>

        <div className="sidebar-divider-bottom" />

        {user && (
          <div className="sidebar-profile">
            <img
              src={profilePic}
              alt="Profile"
              className="sidebar-profile-pic"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/defaultProfile.png";
              }}
            />
            <div className="sidebar-profile-info">
              <div className="sidebar-profile-name">{displayName}</div>
            </div>
          </div>
        )}
      </div>

      {showDeleteModal && (
        <div className="delete-modal-backdrop">
          <div className="delete-modal">
            <h2>Delete chat?</h2>
            <p>This will delete <strong>{chatToDeleteTitle}</strong>.</p>
            <p> - Removing memories saved during this chat.</p>
            <div className="modal-buttons">
              <button className="cancel-btn" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="delete-btn" onClick={executeDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sidebar;
